import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useTheme } from '@/context/ThemeContext';
import CountUp from 'react-countup';

const stats = [
  { value: 73, suffix: '%', label: 'website bisnis Indonesia gagal mengonversi' },
  { value: 47, prefix: 'Rp ', suffix: ' Jt', label: 'rata-rata terbuang untuk website tanpa tujuan' },
  { value: 82, suffix: '%', label: 'pengunjung meninggalkan website dalam 15 detik' },
];

const painPoints = [
  { title: 'Website "Brosur Digital"', desc: 'Cantik tapi tidak menghasilkan. Tidak ada strategi konversi, hanya informasi statis tanpa arah.' },
  { title: 'Copy-Paste Template', desc: 'Website yang mirip ribuan lainnya. Tidak ada diferensiasi, tidak ada cerita, tidak ada alasan untuk kembali.' },
  { title: 'Investasi Tanpa ROI', desc: 'Jutaan rupiah dibelanjakan tanpa tahu apakah website benar-benar bekerja untuk bisnis.' },
];

export default function ProblemSection() {
  const { theme } = useTheme();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section
      data-testid="problem-section"
      ref={ref}
      className="relative overflow-hidden"
      style={{ backgroundColor: theme === 'dark' ? '#0C1120' : '#F0EDE8' }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 md:py-32 lg:py-40">
        {/* Overline */}
        <motion.div
          className="mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.1 }}
        >
          <span className="font-mono text-xs tracking-[0.2em] uppercase" style={{ color: 'var(--accent-primary)' }}>
            The Conflict
          </span>
        </motion.div>

        <motion.h2
          className="font-heading text-3xl md:text-5xl font-semibold tracking-tight leading-tight max-w-3xl mb-16"
          style={{ color: 'var(--text-primary)' }}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.2 }}
        >
          {theme === 'dark'
            ? 'Mayoritas website bisnis adalah monumen digital yang diam. Indah, tapi tak bernyawa.'
            : 'Data bicara: website tanpa strategi adalah pengeluaran, bukan investasi.'}
        </motion.h2>

        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20">
          {stats.map((stat, i) => (
            <motion.div
              key={i}
              className="glass-card p-8 text-center"
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 + i * 0.15 }}
              data-testid={`problem-stat-${i}`}
            >
              <div className="font-heading text-4xl md:text-5xl font-bold mb-2" style={{ color: 'var(--accent-primary)' }}>
                {inView && (
                  <CountUp
                    start={0}
                    end={stat.value}
                    duration={2}
                    delay={0.5 + i * 0.2}
                    prefix={stat.prefix || ''}
                    suffix={stat.suffix || ''}
                  />
                )}
              </div>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{stat.label}</p>
            </motion.div>
          ))}
        </div>

        {/* Pain Points */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {painPoints.map((point, i) => (
            <motion.div
              key={i}
              className="glass-card p-8 group"
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.6 + i * 0.15 }}
              data-testid={`problem-pain-${i}`}
            >
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center mb-4 font-mono text-sm font-bold"
                style={{
                  background: theme === 'dark' ? 'rgba(255,0,153,0.1)' : 'rgba(217,119,6,0.1)',
                  color: theme === 'dark' ? '#FF0099' : '#D97706',
                }}
              >
                {i + 1}
              </div>
              <h3 className="font-heading text-lg font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
                {point.title}
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                {point.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
